import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { accommodationQuickFacts } from "@/data/resort";
import { useBooking } from "../booking-context";
import { HeroPhoto } from "../OptimizedImage";
import { ResortButton, ResortLink } from "../resort-button";
import { StatCounter } from "../StatCounter";

export function AccommodationsHero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { openBooking } = useBooking();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <section
      ref={ref}
      id="top"
      aria-labelledby="accommodations-hero-heading"
      className="relative min-h-[78svh] overflow-hidden bg-forest-deep md:min-h-[72svh]"
    >
      <motion.div
        className="absolute inset-0"
        style={{ y: reduced ? 0 : bgY }}
        initial={reduced ? false : { scale: 1.06 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease }}
      >
        <HeroPhoto alt="Premium motorhome parked on a spacious full-hookup RV site at Auburn RV Resort" />
        <div className="overlay-cinematic absolute inset-0" />
      </motion.div>

      <motion.div
        style={{ y: reduced ? 0 : contentY }}
        className="shell relative flex min-h-[78svh] flex-col justify-end pb-28 pt-18 md:min-h-[72svh] md:pb-28 md:pt-20"
      >
        <motion.p
          className="eyebrow text-primary-foreground/80"
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
        >
          Your Space in the Sierra Foothills
        </motion.p>

        <h1
          id="accommodations-hero-heading"
          className="display-xl mt-6 max-w-[16ch] text-primary-foreground"
        >
          {["RV Sites Built", "for the Way", "You Travel"].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="display-xl block"
                initial={reduced ? false : { y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.1, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-2xl text-base leading-relaxed text-primary-foreground/80 md:text-lg"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease }}
        >
          From convenient pull-through spaces to spacious back-in and accessible sites, Auburn RV
          Resort offers full-hookup accommodations designed for overnight visits, extended stays and
          everything in between.
        </motion.p>

        <motion.div
          className="mt-10 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease }}
        >
          <ResortButton variant="gold" onClick={openBooking} className="w-full sm:w-auto">
            Check Availability
          </ResortButton>
          <ResortLink variant="onDark" href="#site-types" className="w-full sm:w-auto">
            Compare Site Types
          </ResortLink>
        </motion.div>
      </motion.div>

      <div className="pointer-events-none absolute bottom-36 right-6 hidden items-center gap-3 text-primary-foreground/70 md:flex md:bottom-40">
        <span className="eyebrow">Scroll</span>
        <motion.span
          animate={reduced ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="size-4" strokeWidth={1.5} />
        </motion.span>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 translate-y-[42%] md:translate-y-1/2">
        <div className="shell pb-[4.5rem] md:pb-0">
          <dl className="grid grid-cols-2 overflow-hidden rounded-sm border border-border/60 bg-card/95 shadow-soft backdrop-blur-md md:grid-cols-5">
            {accommodationQuickFacts.map((fact, i) => {
              const isLast = i === accommodationQuickFacts.length - 1;
              return (
                <div
                  key={fact.label}
                  className={`flex h-full flex-col-reverse items-center justify-center border-border/60 px-4 py-6 text-center md:border-r md:last:border-r-0 md:px-5 md:py-7 ${
                    isLast ? "col-span-2 md:col-span-1" : ""
                  } ${!isLast && i % 2 === 0 ? "border-r" : ""} ${!isLast ? "border-b md:border-b-0" : ""}`}
                >
                  <dt className="eyebrow mt-3 text-[0.6rem] text-muted-foreground md:text-[0.68rem]">
                    {fact.label}
                  </dt>
                  <dd className="font-display text-2xl leading-none tracking-tight text-primary md:text-3xl lg:text-4xl">
                    {"value" in fact && typeof fact.value === "number" ? (
                      <StatCounter value={fact.value} suffix={fact.suffix} startOnMount />
                    ) : (
                      <span className="block text-lg leading-tight md:text-xl lg:text-2xl">
                        {fact.text}
                      </span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </section>
  );
}
